import React, {useState, useEffect, useRef, useCallback} from 'react';
import AsyncSelect from 'react-select';
import Spinner from "@/components/tailwindComponents/Spinner";

const InputAsync = ({
                        name,
                        label,
                        loadData,
                        size = "sm",
                        disabled = false,
                        defaultValue = [],
                        loadDataOptions = "",
                        multiSelect = false,
                        required = false,
                        defaultHasMore = true,
                        pageSize = 10,
                        showInitialOption = false,
                        onChange,
                        value,
                        error,
                    }) => {
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(defaultHasMore);
    const [selectedValue, setSelectedValue] = useState(defaultValue);
    const selectRef = useRef(null);

    // وقتی prop value تغییر کرد، مقدار selectedValue آپدیت بشه
    useEffect(() => {
        if (value !== undefined) {
            setSelectedValue(value);
        }
    }, [value]);

    // گرفتن مقدار اولیه اگر defaultValue خالی نباشد
    useEffect(() => {
        if (value !== undefined) {
            setSelectedValue(value);
        } else if (defaultValue && defaultValue.length > 0) {
            setSelectedValue(defaultValue);
        } else {
            // لود اولیه دیتا فقط یکبار، بدون وابستگی به loadData و بقیه
            (async () => {
                setLoading(true);
                try {
                    const result = await loadData("", 1, showInitialOption, loadDataOptions);
                    setOptions(result);
                    setHasMore(result.length === pageSize);
                } catch (e) {
                    console.error(e);
                } finally {
                    setLoading(false);
                }
            })();
        }
    }, [value, defaultValue]);

    // تابع fetch دیتا
    const fetchData = useCallback(async (query, pageNum) => {
        setLoading(true);
        try {
            const result = await loadData(query, pageNum, showInitialOption, loadDataOptions);
            setOptions(prev => (pageNum === 1 ? result : [...prev, ...result]));
            setHasMore(result.length === pageSize);
        } catch (err) {
            console.error("Error fetching options:", err);
        } finally {
            setLoading(false);
        }
    }, [loadData, loadDataOptions, pageSize, showInitialOption]);

    // اسکرول منو (برای infinite scroll)
    const handleMenuScroll = async (event) => {
        if (!hasMore || loading) return;
        const target = event.target;
        const bottom = Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) < 1;
        if (bottom && hasMore && !loading) {
            const nextPage = page + 1;
            await fetchData(inputValue, nextPage);
            setPage(nextPage);
        }
    };

    // پاک کردن انتخاب‌ها
    const handleClear = () => {
        setInputValue("");
        setSelectedValue(multiSelect ? [] : null);
        setOptions([]);
        setPage(1);
        setHasMore(defaultHasMore);
        if (onChange) {
            onChange(multiSelect ? [] : null);
        }
        if (selectRef.current) selectRef.current.blur();
    };

    // استایل‌ها (مثل قبلی)
    const customStyles = {
        control: (base) => ({
            ...base,
            minHeight: size === "sm" ? "2rem" : "2.5rem",
            borderRadius: "0.5rem",
            border: error ? "1px solid #EF4444" : "1px solid #D1D5DB",
            boxShadow: "none",
            backgroundColor: disabled ? "#E5E7EB" : "white",
            "&:hover": {
                border: error ? "1px solid #EF4444" : "1px solid #D1D5DB",
            },
        }),
        menu: (base) => ({
            ...base,
            zIndex: 9999,
            maxHeight: "300px",
            overflowY: "auto",
        }),
        menuPortal: (base) => ({...base, zIndex: 9999}),
        option: (base) => ({
            ...base,
            padding: size === "sm" ? "0.5rem" : "0.75rem",
        }),
        input: (base) => ({
            ...base,
            padding: size === "sm" ? "0.35rem" : "0.5rem",
        }),
    };

    return (
        <div className="w-full my-4">
            <div className="w-full relative">
                <label
                    className="absolute -top-2.5 px-1 bg-white right-3 z-10 block text-sm font-semibold rounded-lg"
                    htmlFor={name}
                >
                    {label} {required && <span className="text-red-500 font-light">*</span>}
                </label>
                <div className="relative">
                    <AsyncSelect
                        ref={selectRef}
                        inputValue={inputValue}
                        onInputChange={(value) => {
                            setInputValue(value);
                            setPage(1);
                            setHasMore(defaultHasMore);
                            if (value.length >= 2 || value.length === 0) {
                                fetchData(value, 1);
                            }
                        }}
                        options={options}
                        isMulti={multiSelect}
                        isDisabled={disabled}
                        value={selectedValue}
                        onChange={(value) => {
                            setSelectedValue(value);
                            if (onChange) onChange(value);
                        }}
                        onMenuOpen={() => {
                            if (options.length === 0 && !loading) {
                                fetchData("", 1);
                            }
                        }}
                        onMenuClose={() => {
                            setPage(1);
                            setOptions([]);
                        }}
                        onMenuScrollToBottom={handleMenuScroll}
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        placeholder="جستجو..."
                        className={`react-select-container text-center ${size === "sm" ? "text-sm" : "text-lg"}`}
                        classNamePrefix="react-select"
                        noOptionsMessage={() => loading ? <Spinner className="size-5"/> : "یافت نشد..."}
                        loadingMessage={() => "جستجو..."}
                        menuPortalTarget={typeof window !== "undefined" ? document.body : null}
                        menuPlacement="auto"
                        menuPosition="fixed"
                        styles={customStyles}
                        components={{
                            LoadingIndicator: () => (
                                <svg
                                    className="animate-spin h-5 w-5 text-gray-500 mr-3"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    />
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    />
                                </svg>
                            ),
                        }}
                    />
                    {(inputValue || (Array.isArray(selectedValue) ? selectedValue.length > 0 : selectedValue)) && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="absolute left-11 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            aria-label="Clear input"
                        >
                            <svg
                                className="h-4 w-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    )}
                </div>
                {error && (
                    <p className="text-red-500 text-xs mt-2">{error}</p>
                )}
            </div>
        </div>
    );
};

export default InputAsync;